/**
 * Mining: what a docked fleet is worth, and what is still undecided.
 *
 * ## The one rule this file exists to enforce
 *
 * **Production is a function of Power and time. Nothing else.** Not the number
 * of beams drawn, not the number of impact sparks, not how long the tab was
 * open, not how many times the player clicked. The scene draws one beam per
 * ship because that is what a fleet mining a core looks like - and if it drew
 * a hundred, the account would earn exactly the same amount.
 *
 * That separation is the whole design. A reward that came from a visual effect
 * would be a reward a client could manufacture, and there is no version of
 * this where the browser gets to say how much ore it produced.
 *
 * ## Integers only
 *
 * Ore is a quantity a player can hold, spend and eventually trade, so it is a
 * `bigint` in whole units and the rate is expressed in parts per million of a
 * unit per second. A rate held as a double would drift, and two accounts with
 * identical fleets would slowly disagree about what they had earned.
 *
 * ## What is NOT decided here
 *
 * Everything with a real economic consequence is marked PENDING and carries a
 * provisional value that is deliberately conservative. Filling these in is a
 * design decision, not an implementation detail, and inventing them quietly is
 * how an economy ends up tuned by whoever typed fastest.
 */

/**
 * The ore types that exist at the start.
 *
 * PROVISIONAL. One type, deliberately: a second type needs a reason for the
 * player to prefer it, and that reason is a design decision nobody has made.
 * The shape is a list so adding the second is a data change rather than a
 * rewrite of everything that assumes there is only one.
 */
export const MINING_ORE_TYPES = ['raw_ore'] as const;

export type MiningOreType = (typeof MINING_ORE_TYPES)[number];

export const DEFAULT_ORE_TYPE: MiningOreType = 'raw_ore';

/** Parts per million, so the rate is an integer that never rounds to zero. */
export const ORE_MICRO_PER_UNIT = 1_000_000n;

/**
 * How much ore one point of Power produces per second, in micro-units.
 *
 * PENDING - the single most consequential number in the feature.
 *
 * Provisionally 1000 micro-ore per Power per second: a fleet with 100 total
 * Power makes 0.1 ore a second, so six an hour. That is deliberately slow.
 * Mining is meant to be worth doing while away, and a rate set too high turns
 * the hunt into the wrong way to play - which is a mistake that is very hard
 * to take back once players have banked it.
 *
 * Configurable rather than hard-coded into the service so a decision can be
 * applied without touching the settlement logic that has to stay exact.
 */
export const ORE_MICRO_PER_POWER_PER_SECOND = 1_000n;

/**
 * How much ore an operation can hold before it stops producing.
 *
 * PENDING. Provisionally 86 400 seconds of a 100-Power fleet - a day of
 * production - expressed as a flat cap rather than as a formula, because
 * whether the cap should scale with the fleet is itself a design question. A
 * cap that grows with Power rewards the same thing twice.
 *
 * The cap does not destroy anything: production simply stops at the ceiling,
 * and the player collects to make room. Silently discarding earned ore would
 * be far worse than pausing.
 */
export const ORE_STORAGE_CAP_MICRO = 8_640_000_000n;

/**
 * How far back a settlement will credit in one go.
 *
 * Six hours, matching the offline-farm cap the hunt already uses. Not a
 * separate decision: two different "how long does an idle account keep
 * earning" answers in one game would be a question nobody could answer.
 */
export const MINING_OFFLINE_CAP_SECONDS = 6 * 60 * 60;

/**
 * How many ships one operation may hold.
 *
 * A SAFETY limit, and nothing else. It exists so a fleet cannot grow without
 * bound in memory, in the settlement's work per transaction, or in the objects
 * the scene allocates - not because a hundred and one ships would be unfair.
 *
 * It was twelve, and twelve was wrong. Twelve was the number that fitted on one
 * ring, which made a drawing decision into an economic rule: the interface said
 * "12/12" and the server refused a thirteenth ship, for no reason a player could
 * be told. Rings now grow to fit the fleet (`ring-layout.ts`), so the geometry
 * follows this number instead of setting it.
 *
 * A hundred is where the five rings the layout builds happen to end - 12, 16,
 * 20, 24 and 28 slots - so the limit and the drawing agree by arithmetic rather
 * than by one of them being rounded to suit the other.
 */
export const MINING_FLEET_LIMIT = 100;

/**
 * Whether rarity or level change production beyond their effect on Power.
 *
 * They do NOT, and that is a decision rather than an omission. A ship's Power
 * already carries its rarity and its level; multiplying by them again would
 * count the same thing twice and make the curve impossible to reason about.
 * If a rarity bonus is wanted later it belongs in Power, where everything else
 * that describes a ship already lives.
 */
export const MINING_RARITY_MULTIPLIER_BPS = 10_000;

/**
 * Fuel.
 *
 * PENDING, and deliberately absent. A cost per second would make mining a
 * decision rather than a free trickle, which is probably right - but it needs
 * a currency, a rate and an answer for what happens when the account runs out,
 * and none of those exist. Modelled as "no fuel is consumed" so the day it is
 * decided, it is an addition rather than a change of meaning.
 */
export const MINING_FUEL_COST_PER_SECOND: null = null;

/**
 * The ore a fleet produces over a whole number of seconds.
 *
 * Exact in `bigint` throughout. The multiplication happens before any division
 * so a fleet too small to make one micro-unit a second still earns over a
 * minute - the alternative rounds small fleets to nothing, which is the same
 * as telling a new player the feature does not work.
 *
 * Seconds, not milliseconds: the settlement floors to whole seconds before it
 * gets here, so a caller cannot manufacture ore by settling a thousand times a
 * second.
 */
export function oreMicroForSeconds(totalPower: bigint, seconds: bigint): bigint {
  if (totalPower <= 0n || seconds <= 0n) {
    return 0n;
  }
  return totalPower * seconds * ORE_MICRO_PER_POWER_PER_SECOND;
}

/** The rate a fleet produces at, in micro-ore per second. For display. */
export function oreMicroPerSecond(totalPower: bigint): bigint {
  return totalPower <= 0n ? 0n : totalPower * ORE_MICRO_PER_POWER_PER_SECOND;
}

/**
 * How much of `produced` actually fits, given what is already stored.
 *
 * Returns what is credited, never what is lost - a caller that wants to say
 * "the store is full" compares the two itself. Splitting it this way is what
 * keeps the cap from quietly deleting ore inside an arithmetic helper.
 */
export function oreThatFits(stored: bigint, produced: bigint, cap: bigint): bigint {
  if (produced <= 0n) {
    return 0n;
  }
  const room = cap - stored;
  if (room <= 0n) {
    return 0n;
  }
  return produced > room ? room : produced;
}

/** Whole ore units, for display. Micro-units are the stored truth. */
export function wholeOre(micro: bigint): bigint {
  return micro / ORE_MICRO_PER_UNIT;
}
